import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { useCallback, useMemo, useState } from 'react';
import { DataGrid, DataGridColumn, DataGridSortModel, TaskDialog, Badge, Spinner } from '@repo/components';
import { Task } from '@repo/shared/domain';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getMyTasks } from 'src/api/personal/tasks';

interface SearchParams {
	sort?: string;
	page?: number;
}

const variants = {
	done: 'success',
	pending: 'info',
	todo: 'default',
	expiryWarning: 'warning',
	expired: 'error',
	failed: 'error',
} as const;

const states: Record<string, string> = {
	done: 'Done',
	pending: 'Pending',
	todo: 'Todo',
	expiryWarning: 'Almost Expiring',
	expired: 'Expired',
	failed: 'Failed',
	superseded: 'Superseded',
};

export const Route = createFileRoute('/personal_/tasks')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		sort: (search.sort as string) || 'updatedAt:desc',
		page: (search.page as number) || 0,
	}),
	component: TasksPage,
});

function TasksPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = useSearch({ from: '/personal_/tasks' });
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const [selectedTask, setSelectedTask] = useState<Task | null>(null);

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Tasks');

	const queryParams = useMemo(() => {
		const params: Record<string, string> = {
			all: 'true',
			sort: search.sort || 'updatedAt:desc',
		};
		if (search.page) params.page = search.page;
		return params;
	}, [search]);

	const {
		data: tasksData,
		isLoading: tasksLoading,
		refetch: refetchTasks,
	} = useQuery({
		queryKey: ['personal-my-tasks', queryParams],
		queryFn: () => getMyTasks(queryParams),
	});

	const tasks = tasksData?.data || [];

	const updateQueryParams = useCallback(
		(params: Partial<SearchParams>) => {
			navigate({
				to: '/personal/tasks',
				search: (prev) => ({ ...prev, ...params }),
			});
		},
		[navigate],
	);

	const handleSort = ([sort]: DataGridSortModel) => {
		if (sort) {
			updateQueryParams({ sort: sortToString(sort) });
		}
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleTaskDialogClose = () => {
		setSelectedTask(null);
		refetchTasks();
		queryClient.invalidateQueries({ queryKey: ['personal-my-tasks'] });
	};

	const handleRowClick = ({ row }: { row: Task }) => {
		const isPlayitTask = row.type === 'playit';
		if (row.status !== 'todo' && !isPlayitTask) return;
		setSelectedTask(row);
	};

	const filteredData = useMemo(
		() => tasks.filter((d) => ['todo', 'expiryWarning', 'pending', 'done'].includes(d.status)),
		[tasks],
	);

	const columns: DataGridColumn<Task>[] = [
		{
			headerName: 'Description',
			field: 'name',
			flex: 1,
		},
		{
			field: 'createdAt',
			headerName: 'Assigned',
			width: 120,
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
		{
			field: 'updatedAt',
			headerName: 'Last updated',
			width: 120,
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
		{
			field: 'validTo',
			headerName: 'Valid to',
			width: 100,
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
		{
			field: 'status',
			headerName: 'Status',
			renderCell: (cell: { row: Task }) => (
				<Badge variant={variants[cell.row.status as keyof typeof variants] || 'default'}>
					{states[cell.row.status] ?? cell.row.status}
				</Badge>
			),
			sortable: false,
			width: 175,
		},
	];

	if (authLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<div className="px-4 flex justify-center">
				<Paper variant="outlined" className="mt-2 max-w-[1280px] w-full">
					<DataGrid
						data={filteredData}
						columns={columns}
						rowCount={tasksData?.count ?? 0}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						isLoading={tasksLoading}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort((search.sort || 'updatedAt:desc') as `${string}:${string}`)]}
						pageSize={tasksData?.pageSize ?? 25}
						pageSizeOptions={[tasksData?.pageSize ?? 25]}
						page={tasksData?.page ?? 0}
						onRowClick={handleRowClick}
						pagination
						getRowClassName={(p) => {
							const clickable = p.row.status === 'todo' || p.row.type === 'playit';
							const classes = [
								p.row.markedForReview ? 'error' : '',
								!clickable ? 'cursor-default hover:bg-transparent' : '',
							].filter(Boolean);
							return classes.join(' ');
						}}
						onReload={() => {
							refetchTasks();
							queryClient.invalidateQueries({ queryKey: ['personal-my-tasks'] });
						}}
					/>
				</Paper>
			</div>
			<TaskDialog task={selectedTask} onClose={handleTaskDialogClose} userId={authenticatedUser?.id ?? ''} />
		</>
	);
}
